import React, { useEffect, useState, useContext, useRef, useLayoutEffect, useCallback } from 'react';

import log from 'modules/log';
import StateContext from 'contexts/StateContext';
import DispatchContext from 'contexts/DispatchContext';
import { useAtom } from 'jotai';
import { permissionsAtom } from '../store/media';
import Logger from 'modules/logger';

function usePrevious(value) {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
}

function useTimeout(callback, delay) {
	const timeoutRef = useRef(null);
	const savedCallback = useRef(callback);

	// Remember the latest function.
	useEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	// Set up the interval.
	useEffect(() => {
		const tick = () => savedCallback.current();

		if (delay !== null) {
			let id = setTimeout(tick, delay);
			return () => clearTimeout(id);
		}
	}, [delay]);
	return timeoutRef;
}

function useLocalStorage(key, initialValue) {
	// State to store our value
	// Pass initial state function to useState so logic is only executed once
	const [storedValue, setStoredValue] = useState(() => {
		try {
			// Get from local storage by key
			const item = window.localStorage.getItem(key);
			// Parse stored json or if none return initialValue
			return item ? JSON.parse(item) : initialValue;
		} catch (error) {
			// If error also return initialValue
			console.log(error);
			return initialValue;
		}
	});

	// Return a wrapped version of useState's setter function that ...
	// ... persists the new value to localStorage.
	const setValue = (value) => {
		try {
			// Allow value to be a function so we have same API as useState
			const valueToStore = value instanceof Function ? value(storedValue) : value;
			// Save state
			setStoredValue(valueToStore);
			// Save to local storage
			window.localStorage.setItem(key, JSON.stringify(valueToStore));
		} catch (error) {
			// A more advanced implementation would handle the error case
			console.log(error);
		}
	};

	return [storedValue, setValue];
}

function useInterval(callback, delay) {
	const savedCallback = useRef();

	// Remember the latest function.
	useEffect(() => {
		savedCallback.current = callback;
	}, [callback]);

	// Set up the interval.
	useEffect(() => {
		function tick() {
			savedCallback.current();
		}
		if (delay !== null) {
			let id = setInterval(tick, delay);
			return () => clearInterval(id);
		}
	}, [delay]);
}

function useWindowSize() {
	// https://stackoverflow.com/questions/19014250/rerender-view-on-browser-resize-with-react
	const [size, setSize] = useState([0, 0]);
	useLayoutEffect(() => {
		function updateSize() {
			setSize([window.innerWidth, window.innerHeight]);
		}
		window.addEventListener('resize', updateSize);
		updateSize();
		return () => window.removeEventListener('resize', updateSize);
	}, []);
	return size;
}

const useRecordingStatus = () => {
	const state = useContext(StateContext);
	const dispatch = useContext(DispatchContext);

	const handleRecordingSignal = useCallback((message) => {
		console.log('[sessionRecording]', { message });
		switch (message) {
			case 'start':
				dispatch({
					type: 'sessionStartRecordingCountdown',
				});
				break;
			case 'stop':
				dispatch({
					type: 'sessionStopRecording',
				});
				break;
		}
	}, []);

	return { sessionRecording: state.sessionRecording, handleRecordingSignal };
};

const useLogs = () => {
	const state = useContext(StateContext);
	const [permissions, setPermissions] = useAtom(permissionsAtom);

	const getMediaErrorLogData = (result, deviceList) => {
		const { error, errors, constraints } = result;
		let audioInfo;
		let videoInfo;
		const audioDevice = deviceList.find((d) => d.deviceId === constraints.audio?.deviceId?.exact);
		const videoDevice = deviceList.find((d) => d.deviceId === constraints.video?.deviceId?.exact);
		// if (audioDevice) {
		// 	audioInfo = audioDevice.getCapabilities();
		// 	audioInfo.label = audioDevice.label;
		// }
		// if (videoDevice) {
		// 	videoInfo = videoDevice.getCapabilities();
		// 	videoInfo.label = videoDevice.getCapabilities();
		// }
		return {
			constraints: constraints,
			audioDeviceInfo: audioDevice,
			videoDeviceInfo: videoDevice,
			errors: errors,
			error: error,
			plugins: navigator.plugins,
			errorType: error?.name,
		};
	};

	const logMediaStream = async (localMediaStream) => {
		let logData = {
			audio: false,
			video: false,
		};

		if (localMediaStream.hasVideo) {
			logData.video = {
				muted: localMediaStream.isVideoMuted,
				details: localMediaStream.getVideoDeviceDetails(),
			};
		}

		if (localMediaStream.hasAudio) {
			logData.audio = {
				muted: localMediaStream.isAudioMuted,
				details: localMediaStream.getAudioDeviceDetails(),
			};
		}

		log(logData, 'gotMediaStream', 'info', state.currentParticipant.role);
		Logger.log(
			Logger.INFO,
			`User with role [${state.currentParticipant.role}] got a media stream.`,
			'hooks.js',
			'logMediaStream',
			'GOT_MEDIA_STREAM',
			{ logData }
		);
	};

	const logUserMediaRequest = async (result) => {
		const deviceList = await navigator.mediaDevices.enumerateDevices();
		const logData = getMediaErrorLogData(result, deviceList);
		logData.type = result.reason;
		logData.errorMessage = result.errorMessage;
		logData.inGreenRoom = state.currentParticipant.inGreenRoom;
		logData.settingsDialogOpen = state.settingsDialogOpen;
		logData.permissions = permissions;
		logData.mediaCoreVersion = 2;
		if (!result.error) {
			log(logData, 'userMediaRequest', 'info', state.currentParticipant.role);
			Logger.log(
				Logger.INFO,
				`User with role [${state.currentParticipant.role}] made a user media request.`,
				'hooks.js',
				'logUserMediaRequest',
				'USER_MEDIA_REQUEST',
				{ logData }
			);
		} else {
			log(logData, 'getUserMediaError', 'error', state.currentParticipant.role);
			Logger.log(
				Logger.ERROR,
				`User with role [${state.currentParticipant.role}] encountered a getUserMedia (media request) error!`,
				'hooks.js',
				'logUserMediaRequest',
				'USER_MEDIA_ERROR',
				{ logData }
			);
		}
	};

	const logJoinedStudio = () => {
		const logData = { participantData: state.currentParticipant };
		logData.mediaCoreVersion = 2;
		log(logData, 'userJoinedStudio', 'info', state.currentParticipant.role);
		Logger.log(
			Logger.INFO,
			`User with role [${state.currentParticipant.role}] Joined studio.`,
			'hooks.js',
			'logJoinedStudio',
			'USER_JOINED_STUDIO',
			{ logData }
		);
	};

	const logMicToggled = (value) => {
		const logData = { audioRequested: value, permissions: permissions };
		log(logData, 'micToggled', 'info', state.currentParticipant.role);
		logData.mediaCoreVersion = 2;
		Logger.log(
			Logger.INFO,
			`User with role [${state.currentParticipant.role}] toggled the large mic icon (on control panel).`,
			'hooks.js',
			'logMicToggled',
			'USER_TOGGLED_MIC',
			{ logData }
		);
	};

	const logCamToggled = (value) => {
		const logData = { videoRequested: value, permissions: permissions };
		log(logData, 'camToggled', 'info', state.currentParticipant.role);
		logData.mediaCoreVersion = 2;
		Logger.log(
			Logger.INFO,
			`User with role [${state.currentParticipant.role}] toggled the large cam icon (on control panel).`,
			'hooks.js',
			'logCamToggled',
			'USER_TOGGLED_CAM',
			{ logData }
		);
	};

	const logContinueWithoutDevices = async () => {
		const deviceList = await navigator.mediaDevices.enumerateDevices();
		const logData = { deviceList: deviceList, permissions: permissions };
		log(logData, 'noDevicesSelected', 'info', state.currentParticipant.role);
		logData.mediaCoreVersion = 2;
		Logger.log(
			Logger.INFO,
			`User with role [${state.currentParticipant.role}] opted to continue without devices.`,
			'hooks.js',
			'LogContinueWithoutDevices',
			'USER_CONTINUED_WITHOUT_DEVICES',
			{ logData }
		);
	};

	const logEchoCancellationToggled = (echoCancellation, participant, isCurrentUser) => {
		const logData = { echoCancellation, participant, isCurrentUser };
		log(logData, 'echoCancellationToggled', 'info', state.currentParticipant.role);
		logData.mediaCoreVersion = 2;
		Logger.log(
			Logger.INFO,
			`User with role [${state.currentParticipant.role}] toggled echo cancellation.`,
			'hooks.js',
			'logEchoToggled',
			'USER_TOGGLED_ECHO',
			{ logData }
		);
	};

	const logActiveDeviceLost = ({ device }) => {
		const logData = { device, participant: state.currentParticipant };
		log(logData, 'logActiveDeviceLost', 'info', state.currentParticipant.role);
		logData.mediaCoreVersion = 2;
		Logger.log(
			Logger.WARN,
			`User with role [${state.currentParticipant.role}] lost connection with active device [${device.label}].`,
			'hooks.js',
			'logActiveDeviceLost',
			'ACTIVE_DEVICE_LOST',
			{ logData }
		);
	};

	const logDeviceList = ({ devices }) => {
		const logData = { devices, participant: state.currentParticipant };
		log(logData, 'logDeviceList', 'info', state.currentParticipant.role);
		logData.mediaCoreVersion = 2;
		Logger.log(
			Logger.INFO,
			`There are [${devices.length}] devices available.`,
			'hooks.js',
			'logDeviceList',
			'LIST_DEVICES',
			{ logData }
		);
	};

	const logPublishingPermissionsError = (constraints, permissions) => {
		const logData = { constraints, permissions };
		logData.mediaCoreVersion = 2;
		// log(logData, 'publisherPermissionsFailure', 'error', state.currentParticipant.role);
		Logger.log(
			Logger.ERROR,
			`User with role [${state.currentParticipant.role}] encountered a permission error when publishing .`,
			'hooks.js',
			'logPublishPermissionsError',
			'USER_HAD_PERMISSION_ERROR_WHEN_PUBLISHING',
			{ logData }
		);
	};

	const logCameraPublished = (mediaStream) => {
		const logData = { audioTrack: false, videoTrack: false, mediaStream: mediaStream };
		logData.mediaCoreVersion = 2;
		if (mediaStream) {
			logData.audioTrack = !!mediaStream.getAudioTracks()[0];
			logData.videoTrack = !!mediaStream.getVideoTracks()[0];
		}
		Logger.log(
			Logger.INFO,
			`User with role [${state.currentParticipant.role}] published a stream.`,
			'hooks.js',
			'logStreamPublished',
			'USER_PUBLISHED_SUCCESSFULLY',
			{ logData }
		);
	};

	const logCameraPublishError = (error, type) => {
		const logData = { error: error, activeDevices: state.currentParticipant.activeDevices, type: type };
		logData.mediaCoreVersion = 2;
		//log(logData, type, 'error', state.currentParticipant.role);
		Logger.log(
			Logger.ERROR,
			`User with role [${state.currentParticipant.role}] encountered a publish error (open tok).`,
			'hooks.js',
			'logStreamPublishError',
			'USER_HAD_PUBLISH_ERROR',
			{ logData }
		);
	};

	const logTrackStopped = async (e, reason, type) => {
		const devices = await navigator.mediaDevices.enumerateDevices();
		const logData = {
			event: e,
			type,
			reason,
			enumeratedDevices: devices,
			participantData: state.currentParticipant,
		};
		logData.mediaCoreVersion = 2;
		// log(logData, type, 'error', state.currentParticipant.role);
		Logger.log(
			Logger.INFO,
			`User with role [${state.currentParticipant.role}] had a track stopped event.`,
			'hooks.js',
			'logTrackStopped',
			'USER_TRACK_STOPPED',
			{ logData }
		);
	};

	const logPermissionsChanged = async (permissions, prevPermissions) => {
		const logData = {
			permissions,
			prevPermissions,
			participantData: state.currentParticipant,
		};
		logData.mediaCoreVersion = 2;
		// log(logData, type, 'error', state.currentParticipant.role);
		Logger.log(
			Logger.INFO,
			`User with role [${state.currentParticipant.role}] changed permissions.`,
			'hooks.js',
			'logPermissionsChanged',
			'USER_TRACK_STOPPED',
			{ logData }
		);
	};

	return {
		logUserMediaRequest,
		logJoinedStudio,
		logMicToggled,
		logCamToggled,
		logContinueWithoutDevices,
		logPublishingPermissionsError,
		logCameraPublished,
		logCameraPublishError,
		logEchoCancellationToggled,
		logTrackStopped,
		logActiveDeviceLost,
		logPermissionsChanged,
		logDeviceList,
		logMediaStream,
	};
};

export { usePrevious, useLocalStorage, useInterval, useWindowSize, useRecordingStatus, useLogs, useTimeout };
